import React, { useEffect, useState } from 'react';
import { AlertTriangleIcon, PlusIcon, EditIcon, TrashIcon, CheckCircleIcon } from 'lucide-react';
import { OrgUnitSelector } from '../orgunit/OrgUnitSelector';
import type { OrgUnit } from '../../financial/sharedFinancialData';
type PrecisionScope = 'Global' | 'OrgUnit' | 'Vendor';
type PrecisionSetting = {
  id: string;
  scope: PrecisionScope;
  scopeId?: string;
  scopeName?: string;
  scopePath?: string;
  precision: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};
export function DecimalPrecisionConfiguration({
  locale = 'en-US'
}) {
  const [precisionSettings, setPrecisionSettings] = useState<PrecisionSetting[]>([{
    id: 'prec-001',
    scope: 'Global',
    precision: 2,
    isActive: true,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'prec-002',
    scope: 'OrgUnit',
    scopeId: 'ou-apac-sg',
    scopeName: 'Singapore Office',
    scopePath: 'Global Technologies Inc. > APAC Region > Singapore Office',
    precision: 4,
    isActive: true,
    createdAt: '2023-03-15T00:00:00Z',
    updatedAt: '2023-03-15T00:00:00Z'
  }, {
    id: 'prec-003',
    scope: 'OrgUnit',
    scopeId: 'ou-apac-tokyo',
    scopeName: 'Tokyo Office',
    scopePath: 'Global Technologies Inc. > APAC Region > Tokyo Office',
    precision: 2,
    isActive: true,
    createdAt: '2023-04-01T00:00:00Z',
    updatedAt: '2023-04-01T00:00:00Z'
  }, {
    id: 'prec-004',
    scope: 'Vendor',
    scopeId: 'vendor-001',
    scopeName: 'Office Depot',
    precision: 4,
    isActive: true,
    createdAt: '2023-05-01T00:00:00Z',
    updatedAt: '2023-05-01T00:00:00Z'
  }, {
    id: 'prec-005',
    scope: 'Vendor',
    scopeId: 'vendor-energy',
    scopeName: 'Energy Supplier Co.',
    precision: 6,
    isActive: true,
    createdAt: '2023-06-01T00:00:00Z',
    updatedAt: '2023-06-01T00:00:00Z'
  }]);
  const [showForm, setShowForm] = useState(false);
  const [editingSetting, setEditingSetting] = useState<PrecisionSetting | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Form state
  const [formScope, setFormScope] = useState<PrecisionScope>('OrgUnit');
  const [formPrecision, setFormPrecision] = useState(2);
  const [formScopeId, setFormScopeId] = useState('');
  const [formOrgUnit, setFormOrgUnit] = useState<OrgUnit | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Sample vendors for dropdown
  const [vendors] = useState([{
    id: 'vendor-001',
    name: 'Office Depot'
  }, {
    id: 'vendor-002',
    name: 'Dell Technologies'
  }, {
    id: 'vendor-003',
    name: 'PrintWorks Inc.'
  }, {
    id: 'vendor-004',
    name: 'Acme Construction'
  }, {
    id: 'vendor-005',
    name: 'Global Logistics Partners'
  }, {
    id: 'vendor-energy',
    name: 'Energy Supplier Co.'
  }]);
  const handleAddNew = () => {
    setEditingSetting(null);
    setFormScope('OrgUnit');
    setFormPrecision(2);
    setFormScopeId('');
    setFormOrgUnit(null);
    setFormErrors({});
    setShowForm(true);
  };
  const handleEdit = (setting: PrecisionSetting) => {
    setEditingSetting(setting);
    setFormScope(setting.scope);
    setFormPrecision(setting.precision);
    setFormScopeId(setting.scopeId || '');
    setFormErrors({});
    setShowForm(true);
  };
  const handleDelete = (id: string) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (deletingId) {
      setPrecisionSettings(precisionSettings.filter(s => s.id !== deletingId));
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };
  const handleToggleActive = (id: string) => {
    setPrecisionSettings(precisionSettings.map(s => s.id === id ? {
      ...s,
      isActive: !s.isActive,
      updatedAt: new Date().toISOString()
    } : s));
  };
  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (formScope === 'OrgUnit' && !formScopeId) {
      errors.scopeId = 'Organisation Unit is required';
    }
    if (formScope === 'Vendor' && !formScopeId) {
      errors.scopeId = 'Vendor is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSave = () => {
    if (!validateForm()) return;
    const vendor = vendors.find(v => v.id === formScopeId);
    if (editingSetting) {
      setPrecisionSettings(precisionSettings.map(s => s.id === editingSetting.id ? {
        ...s,
        scope: formScope,
        scopeId: formScope !== 'Global' ? formScopeId : undefined,
        scopeName: formScope === 'OrgUnit' ? formOrgUnit?.name : formScope === 'Vendor' ? vendor?.name : undefined,
        scopePath: formScope === 'OrgUnit' ? formOrgUnit?.path : undefined,
        precision: formPrecision,
        updatedAt: new Date().toISOString()
      } : s));
    } else {
      const newSetting: PrecisionSetting = {
        id: `prec-${Date.now()}`,
        scope: formScope,
        scopeId: formScope !== 'Global' ? formScopeId : undefined,
        scopeName: formScope === 'OrgUnit' ? formOrgUnit?.name : formScope === 'Vendor' ? vendor?.name : undefined,
        scopePath: formScope === 'OrgUnit' ? formOrgUnit?.path : undefined,
        precision: formPrecision,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setPrecisionSettings([...precisionSettings, newSetting]);
    }
    setShowForm(false);
    setEditingSetting(null);
  };
  const handleCancel = () => {
    setShowForm(false);
    setEditingSetting(null);
    setFormErrors({});
  };
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: OrgUnit | null) => {
    setFormScopeId(orgUnitId);
    setFormOrgUnit(orgUnit);
    if (formErrors.scopeId) {
      setFormErrors({
        ...formErrors,
        scopeId: ''
      });
    }
  };
  const handleVendorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormScopeId(e.target.value);
    if (formErrors.scopeId) {
      setFormErrors({
        ...formErrors,
        scopeId: ''
      });
    }
  };
  const handleScopeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newScope = e.target.value as PrecisionScope;
    setFormScope(newScope);
    setFormScopeId('');
    setFormOrgUnit(null);
    setFormErrors({});
  };
  const globalSetting = precisionSettings.find(s => s.scope === 'Global');
  const vendorSettings = precisionSettings.filter(s => s.scope === 'Vendor');
  const orgUnitSettings = precisionSettings.filter(s => s.scope === 'OrgUnit');
  const getPrecisionLabel = (precision: number) => {
    switch (precision) {
      case 2:
        return 'Standard';
      case 4:
        return 'High Precision';
      case 6:
        return 'Ultra Precision';
      default:
        return `${precision} decimals`;
    }
  };
  return <div data-id="element-17455">
      <div className="mb-8" data-id="element-17456">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-17457">
          Decimal Precision Configuration
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-17458">
          Configure decimal places for currency fields at global, vendor, and
          organisation unit levels. Resolution follows override hierarchy:
          Vendor → OrgUnit → Global.
        </p>
      </div>

      {/* Warning Banner */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6" data-id="element-17459">
        <div className="flex" data-id="element-17460">
          <div className="flex-shrink-0" data-id="element-17461">
            <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-17462" />
          </div>
          <div className="ml-3" data-id="element-17463">
            <p className="text-sm text-yellow-700" data-id="element-17464">
              ⚠️ Changing precision settings will affect currency fields across
              RapidP2P. Vendor-specific settings override OrgUnit settings,
              which override the global default. Changes should only be made by
              authorised financial administrators.
            </p>
          </div>
        </div>
      </div>

      {!showForm ? <>
          {/* Global Default Setting */}
          <div className="bg-white shadow rounded-lg mb-6" data-id="element-17465">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-17466">
              <div className="flex items-center justify-between" data-id="element-17467">
                <div data-id="element-17468">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17469">
                    Global Default
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-17470">
                    Default precision applied unless overridden by OrgUnit or
                    Vendor
                  </p>
                </div>
                <div className="flex space-x-2" data-id="element-17471">
                  {globalSetting && <button onClick={() => handleEdit(globalSetting)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17472">
                      <EditIcon className="h-4 w-4 mr-2" data-id="element-17473" />
                      Edit
                    </button>}
                  <button onClick={handleAddNew} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17474">
                    <PlusIcon className="h-4 w-4 mr-2" data-id="element-17475" />
                    Add Override
                  </button>
                </div>
              </div>
            </div>
            {globalSetting && <div className="px-4 py-5 sm:px-6" data-id="element-17476">
                <div className="flex items-center justify-between" data-id="element-17477">
                  <div data-id="element-17478">
                    <span className="text-sm font-medium text-gray-500" data-id="element-17479">
                      Decimal Places:
                    </span>
                    <span className="ml-2 text-2xl font-bold text-gray-900" data-id="element-17480">
                      {globalSetting.precision}
                    </span>
                    <span className="ml-2 text-sm text-gray-500" data-id="element-17481">
                      ({getPrecisionLabel(globalSetting.precision)})
                    </span>
                  </div>
                  <CheckCircleIcon className="h-6 w-6 text-green-500" data-id="element-17482" />
                </div>
              </div>}
          </div>

          {/* OrgUnit Overrides */}
          <div className="bg-white shadow rounded-lg mb-6" data-id="element-17483">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-17484">
              <div className="flex items-center justify-between" data-id="element-17485">
                <div data-id="element-17486">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17487">
                    Organisation Unit Overrides
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-17488">
                    Precision settings for specific organisation units
                    (overrides Global)
                  </p>
                </div>
              </div>
            </div>

            {orgUnitSettings.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-17489">
                <p className="text-sm text-gray-500" data-id="element-17490">
                  No organisation unit overrides configured. All organisation
                  units use the global default.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-17491">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-17492">
                  <thead className="bg-gray-50" data-id="element-17493">
                    <tr data-id="element-17494">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17495">
                        Organisation Unit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17496">
                        Decimal Places
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17497">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17498">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-17499">
                    {orgUnitSettings.map(setting => <tr key={setting.id} data-id="element-17500">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-17501">
                          <div className="text-sm font-medium text-gray-900" data-id="element-17502">
                            {setting.scopeName}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-17503">
                            {setting.scopePath}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-17504">
                          <div className="text-sm font-bold text-gray-900" data-id="element-17505">
                            {setting.precision} decimal places
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-17506">
                            {getPrecisionLabel(setting.precision)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-17507">
                          {setting.isActive ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-17508">
                              <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-17509" />
                              Active
                            </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-17510">
                              Inactive
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-17511">
                          <button onClick={() => handleToggleActive(setting.id)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-17512">
                            {setting.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => handleEdit(setting)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-17513">
                            <EditIcon className="h-4 w-4 inline" data-id="element-17514" />
                          </button>
                          <button onClick={() => handleDelete(setting.id)} className="text-red-600 hover:text-red-900" data-id="element-17515">
                            <TrashIcon className="h-4 w-4 inline" data-id="element-17516" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>

          {/* Vendor-Specific Settings */}
          <div className="bg-white shadow rounded-lg" data-id="element-17517">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-17518">
              <div className="flex items-center justify-between" data-id="element-17519">
                <div data-id="element-17520">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17521">
                    Vendor-Specific Overrides
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-17522">
                    Precision settings for specific vendors (highest priority)
                  </p>
                </div>
              </div>
            </div>

            {vendorSettings.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-17523">
                <p className="text-sm text-gray-500" data-id="element-17524">
                  No vendor-specific precision overrides configured.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-17525">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-17526">
                  <thead className="bg-gray-50" data-id="element-17527">
                    <tr data-id="element-17528">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17529">
                        Vendor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17530">
                        Decimal Places
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17531">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17532">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-17533">
                    {vendorSettings.map(setting => <tr key={setting.id} data-id="element-17534">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-17535">
                          {setting.scopeName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-17536">
                          <div className="text-sm font-bold text-gray-900" data-id="element-17537">
                            {setting.precision} decimal places
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-17538">
                            {getPrecisionLabel(setting.precision)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-17539">
                          {setting.isActive ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-17540">
                              <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-17541" />
                              Active
                            </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-17542">
                              Inactive
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-17543">
                          <button onClick={() => handleToggleActive(setting.id)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-17544">
                            {setting.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => handleEdit(setting)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-17545">
                            <EditIcon className="h-4 w-4 inline" data-id="element-17546" />
                          </button>
                          <button onClick={() => handleDelete(setting.id)} className="text-red-600 hover:text-red-900" data-id="element-17547">
                            <TrashIcon className="h-4 w-4 inline" data-id="element-17548" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>
        </> : <div className="bg-white shadow rounded-lg" data-id="element-17549">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-17550">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17551">
              {editingSetting ? 'Edit Precision Setting' : 'Add Precision Override'}
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6 space-y-6" data-id="element-17552">
            {/* Scope */}
            <div data-id="element-17553">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-17554">
                Scope
              </label>
              <select value={formScope} onChange={handleScopeChange} disabled={editingSetting?.scope === 'Global'} className="block w-full max-w-xs shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed" data-id="element-17555">
                <option value="Global" data-id="element-17556">Global (Default)</option>
                <option value="Vendor" data-id="element-17557">Specific Vendor</option>
                <option value="OrgUnit" data-id="element-17558">Organisation Unit</option>
              </select>
              {editingSetting?.scope === 'Global' && <p className="mt-1 text-xs text-gray-500" data-id="element-17559">
                  Global scope cannot be changed
                </p>}
              <p className="mt-1 text-xs text-gray-500" data-id="element-17560">
                {formScope === 'Global' && 'Default for all entities'}
                {formScope === 'Vendor' && 'Overrides Global and OrgUnit settings'}
                {formScope === 'OrgUnit' && 'Overrides Global settings'}
              </p>
            </div>

            {/* Vendor Selector */}
            {formScope === 'Vendor' && <div data-id="element-17561">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-17562">
                  Vendor <span className="text-red-500" data-id="element-17563">*</span>
                </label>
                <select value={formScopeId} onChange={handleVendorChange} className={`block w-full max-w-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md ${formErrors.scopeId ? 'border-red-300' : ''}`} data-id="element-17564">
                  <option value="" data-id="element-17565">Select a vendor</option>
                  {vendors.map(vendor => <option key={vendor.id} value={vendor.id} data-id="element-17566">
                      {vendor.name}
                    </option>)}
                </select>
                {formErrors.scopeId && <p className="mt-1 text-sm text-red-600" data-id="element-17567">
                    {formErrors.scopeId}
                  </p>}
              </div>}

            {/* OrgUnit Selector */}
            {formScope === 'OrgUnit' && <div data-id="element-17568">
                <OrgUnitSelector value={formScopeId} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} error={formErrors.scopeId} data-id="element-17569" />
              </div>}

            {/* Precision */}
            <div data-id="element-17570">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-17571">
                Decimal Precision
              </label>
              <select value={formPrecision} onChange={e => setFormPrecision(parseInt(e.target.value))} className="block w-full max-w-xs shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md" data-id="element-17572">
                <option value={2} data-id="element-17573">2 Decimal Places (Standard)</option>
                <option value={4} data-id="element-17574">4 Decimal Places (High Precision)</option>
                <option value={6} data-id="element-17575">6 Decimal Places (Ultra Precision)</option>
              </select>
              <p className="mt-1 text-sm text-gray-500" data-id="element-17576">
                Defines how many decimal places are stored and displayed for
                currency values
              </p>
            </div>
          </div>

          <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 space-x-3" data-id="element-17577">
            <button onClick={handleCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17578">
              Cancel
            </button>
            <button onClick={handleSave} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17579">
              {editingSetting ? 'Update' : 'Create'}
            </button>
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-17580">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-17581">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-17582">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-17583"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-17584">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-17585">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-17586">
                <div className="sm:flex sm:items-start" data-id="element-17587">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-17588">
                    <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-17589" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-17590">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17591">
                      Delete Precision Override
                    </h3>
                    <div className="mt-2" data-id="element-17592">
                      <p className="text-sm text-gray-500" data-id="element-17593">
                        Are you sure you want to delete this precision override?
                        The affected scope will revert to using the next level
                        in the hierarchy (Vendor → OrgUnit → Global).
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-17594">
                <button type="button" onClick={confirmDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-17595">
                  Delete
                </button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-17596">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}